import React, { useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { useSelector } from "react-redux";

const EyeOpen = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOff = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const MpinInput = ({ label, value, onChange, show, onToggle, placeholder }) => (
    <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
        <div className="relative">
            <input
                type={show ? "text" : "password"}
                maxLength={6}
                value={value}
                onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
                placeholder={placeholder || "_ _ _ _ _ _"}
                className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-300 focus:border-[#0A2A4F] focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none text-gray-800 font-bold tracking-[0.5em] text-center text-lg bg-white"
            />
            <button
                type="button"
                onClick={onToggle}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
            >
                {show ? <EyeOff /> : <EyeOpen />}
            </button>
        </div>
    </div>
);

const ResetMpin = () => {
    const mid = useSelector((state) => state.auth.employeeDetails?.username);

    const { callApi: changeMpinApi } = useApi("/mpin/change", "POST");
    const { callApi: forgotMpinApi } = useApi("/mpin/forgot", "POST");

    const [activeTab, setActiveTab] = useState("change");

    // Change MPIN
    const [oldMpin, setOldMpin] = useState("");
    const [newMpin, setNewMpin] = useState("");
    const [confirmMpin, setConfirmMpin] = useState("");
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [changeLoading, setChangeLoading] = useState(false);
    const [changeError, setChangeError] = useState("");
    const [changeSuccess, setChangeSuccess] = useState("");

    // Forgot MPIN
    const [forgotNew, setForgotNew] = useState("");
    const [forgotConfirm, setForgotConfirm] = useState("");
    const [showForgotNew, setShowForgotNew] = useState(false);
    const [showForgotConfirm, setShowForgotConfirm] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState("");
    const [forgotSuccess, setForgotSuccess] = useState("");

    // OTP popup
    const [otpPopup, setOtpPopup] = useState({ open: false, pendingPayload: null, sentTo: [] });
    const [otpInput, setOtpInput] = useState("");
    const [otpError, setOtpError] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);

    const validateMpin = (pin) => /^\d{4,6}$/.test(pin);

    const handleChangeMpin = async (e) => {
        e.preventDefault();
        setChangeError("");
        setChangeSuccess("");
        if (!validateMpin(oldMpin)) { setChangeError("Current MPIN must be 4–6 digits"); return; }
        if (!validateMpin(newMpin)) { setChangeError("New MPIN must be 4–6 digits"); return; }
        if (newMpin !== confirmMpin) { setChangeError("MPINs do not match"); return; }
        setChangeLoading(true);
        try {
            const res = await changeMpinApi({ mid, oldMpin, newMpin, confirmMpin });
            if (res?.success) {
                setChangeSuccess(res.message || "MPIN changed successfully");
                setOldMpin(""); setNewMpin(""); setConfirmMpin("");
            } else {
                setChangeError(res?.message || "Failed to change MPIN");
            }
        } catch (err) {
            setChangeError(err?.message || "Something went wrong");
        } finally {
            setChangeLoading(false);
        }
    };

    const handleForgotMpin = async (e) => {
        e.preventDefault();
        setForgotError("");
        setForgotSuccess("");
        if (!validateMpin(forgotNew)) { setForgotError("New MPIN must be 4–6 digits"); return; }
        if (forgotNew !== forgotConfirm) { setForgotError("MPINs do not match"); return; }
        setForgotLoading(true);
        const payload = { mid, newMpin: forgotNew, confirmMpin: forgotConfirm };
        try {
            const res = await forgotMpinApi(payload);
            if (res?.success) {
                setForgotSuccess(res.message || "MPIN reset successfully");
                setForgotNew(""); setForgotConfirm("");
            } else {
                setForgotError(res?.message || "Failed to reset MPIN");
            }
        } catch (err) {
            const errData = err?.responseData || {};
            if (errData?.error_code === "OTP_REQUIRED" && errData?.action_params?.otpRequired === true) {
                setOtpPopup({ open: true, pendingPayload: payload, sentTo: errData.action_params?.sentTo || [] });
                setForgotLoading(false);
                return;
            }
            setForgotError(err?.message || "Something went wrong");
        } finally {
            setForgotLoading(false);
        }
    };

    const handleOtpSubmit = async () => {
        if (otpInput.length !== 6) { setOtpError("Please enter a valid 6-digit OTP"); return; }
        setOtpLoading(true);
        setOtpError("");
        try {
            const res = await forgotMpinApi({ ...otpPopup.pendingPayload, otp: otpInput.trim() });
            setOtpPopup({ open: false, pendingPayload: null, sentTo: [] });
            setOtpInput("");
            setForgotSuccess(res?.message || "MPIN reset successfully");
            setForgotNew(""); setForgotConfirm("");
        } catch (err) {
            setOtpError(err?.message || "Invalid or expired OTP. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    const switchTab = (tab) => {
        setActiveTab(tab);
        setChangeError(""); setChangeSuccess("");
        setForgotError(""); setForgotSuccess("");
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-md mx-auto">

                {/* Header */}
                <div className="mb-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0A2A4F] to-[#1a4a8f] flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <svg width="28" height="28" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800">Reset MPIN</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage your Mobile PIN securely</p>
                </div>

                {/* Tabs */}
                <div className="flex bg-white rounded-xl p-1 shadow-sm border border-gray-200 mb-6">
                    <button
                        onClick={() => switchTab("change")}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === "change" ? "bg-[#0A2A4F] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Change MPIN
                    </button>
                    <button
                        onClick={() => switchTab("forgot")}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === "forgot" ? "bg-[#0A2A4F] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Forgot MPIN
                    </button>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                    {activeTab === "change" ? (
                        <form onSubmit={handleChangeMpin} className="space-y-4">
                            <MpinInput label="Current MPIN" value={oldMpin} onChange={setOldMpin}
                                show={showOld} onToggle={() => setShowOld(!showOld)} />
                            <MpinInput label="New MPIN" value={newMpin} onChange={setNewMpin}
                                show={showNew} onToggle={() => setShowNew(!showNew)} />
                            <MpinInput label="Confirm New MPIN" value={confirmMpin} onChange={setConfirmMpin}
                                show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} />

                            <p className="text-xs text-gray-400">MPIN must be 4–6 digits.</p>

                            {changeError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{changeError}</div>
                            )}
                            {changeSuccess && (
                                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium flex items-center gap-2">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                                    {changeSuccess}
                                </div>
                            )}

                            <button type="submit" disabled={changeLoading}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all">
                                {changeLoading ? (
                                    <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Changing...</>
                                ) : "Change MPIN"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleForgotMpin} className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                                An OTP will be sent to your registered mobile number to verify the reset.
                            </div>

                            <MpinInput label="New MPIN" value={forgotNew} onChange={setForgotNew}
                                show={showForgotNew} onToggle={() => setShowForgotNew(!showForgotNew)} />
                            <MpinInput label="Confirm New MPIN" value={forgotConfirm} onChange={setForgotConfirm}
                                show={showForgotConfirm} onToggle={() => setShowForgotConfirm(!showForgotConfirm)} />

                            <p className="text-xs text-gray-400">MPIN must be 4–6 digits.</p>

                            {forgotError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{forgotError}</div>
                            )}
                            {forgotSuccess && (
                                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium flex items-center gap-2">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                                    {forgotSuccess}
                                </div>
                            )}

                            <button type="submit" disabled={forgotLoading}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all">
                                {forgotLoading ? (
                                    <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Sending OTP...</>
                                ) : "Reset MPIN"}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* OTP Popup */}
            {otpPopup.open && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] px-6 py-4 flex items-center justify-between">
                            <h3 className="text-white font-bold text-base">OTP Verification</h3>
                            <button
                                onClick={() => { setOtpPopup({ open: false, pendingPayload: null, sentTo: [] }); setOtpInput(""); setOtpError(""); }}
                                disabled={otpLoading}
                                className="text-white/70 hover:text-white text-xl leading-none disabled:opacity-40"
                            >✕</button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <p className="text-sm text-gray-600 mb-2">OTP has been sent to:</p>
                                <div className="flex flex-wrap gap-2">
                                    {otpPopup.sentTo.map((num, i) => (
                                        <span key={i} className="px-3 py-1 bg-[#0A2A4F]/10 border border-[#0A2A4F]/20 rounded-full text-sm font-semibold text-[#0A2A4F] tracking-wider">{num}</span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Enter 6-digit OTP</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={otpInput}
                                    onChange={e => { setOtpInput(e.target.value.replace(/\D/g, "")); setOtpError(""); }}
                                    placeholder="_ _ _ _ _ _"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#0A2A4F] focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none font-bold tracking-[0.6em] text-center text-xl"
                                />
                                <p className="text-right text-xs mt-1" style={{ color: otpInput.length === 6 ? "#16a34a" : "#9ca3af" }}>
                                    {otpInput.length} / 6
                                </p>
                            </div>
                            {otpError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{otpError}</div>
                            )}
                            <button
                                onClick={handleOtpSubmit}
                                disabled={otpLoading || otpInput.length !== 6}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {otpLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                                        Verifying...
                                    </span>
                                ) : "Verify & Reset"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResetMpin;
